/* Agrolix Product Studio: scoped controls and Shopify Ajax cart integration. */
if (!customElements.get('agrolix-product-information')) {
 class AgrolixProductInformation extends HTMLElement {
  static queue = Promise.resolve();
  connectedCallback() {
   if (this.abort) return;
   this.buildDescriptionDropdowns();
   this.abort = new AbortController();
   const signal = this.abort.signal;
   this.form = this.querySelector('[data-product-form]');
   this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
   this.dialog = this.querySelector('[data-fallback-drawer]');
   this.zoom = this.querySelector('[data-zoom-dialog]');
   this.plan = this.form.querySelector('[data-plan]');
   this.initialId = String(this.form.elements.id.value);
   this.addEventListener('change', e => {
    if (e.target.name === 'id') this.updateVariant(true);
    if (e.target === this.plan) this.updatePrice(true);
    if (e.target.name === 'quantity') this.updateQuantityButtons();
   }, { signal });
   this.addEventListener('click', e => {
    const thumb = e.target.closest('[data-thumbnail]');
    if (thumb) this.showMedia(thumb.dataset.thumbnail);
    const step = e.target.closest('[data-quantity-change]');
    if (step) {
     const input = this.form.elements.quantity;
     if (!input.validity.valid) input.value = input.min || '1';
     Number(step.dataset.quantityChange) > 0 ? input.stepUp() : input.stepDown();
     this.updateQuantityButtons();
    }
    const zoom = e.target.closest('[data-zoom]');
    if (zoom) {
     const img = this.querySelector('[data-zoom-image]');
     img.src = zoom.dataset.zoomUrl;
     img.alt = zoom.closest('[data-media]').querySelector('img')?.alt || '';
     this.openModal(this.zoom, zoom);
    }
    if (e.target.closest('[data-zoom-close]')) this.zoom.close();
    if (e.target.closest('[data-close]')) this.dialog.close();
    const play = e.target.closest('[data-play]');
    if (play) { const box = play.closest('[data-external]'); box.append(box.querySelector('template').content.cloneNode(true)); play.hidden = true; }
   }, { signal });
   this.addEventListener('keydown', e => {
    const thumb = e.target.closest('[data-thumbnail]');
    if (!thumb || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) return;
    const buttons = [...this.querySelectorAll('[data-thumbnail]')], at = buttons.indexOf(thumb);
    const i = e.key === 'Home' ? 0 : e.key === 'End' ? buttons.length-1 : (at + (['ArrowRight','ArrowDown'].includes(e.key) ? 1 : -1) + buttons.length) % buttons.length;
    e.preventDefault(); buttons[i].focus(); this.showMedia(buttons[i].dataset.thumbnail);
   }, { signal });
   for (const modal of [this.dialog, this.zoom]) {
    modal.addEventListener('click', e => { if(e.target !== modal) return; const r=modal.getBoundingClientRect(); if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)modal.close(); }, {signal});
    modal.addEventListener('close', () => { this.unlockScroll(); this.returnFocus?.focus({preventScroll:true}); }, {signal});
   }
   this.form.addEventListener('submit', e => {
    e.preventDefault();
    if (this.busy || !this.variant?.available || !this.form.reportValidity()) return;
    if (this.variant.requiresPlan && !this.plan?.value) return;
    const item={id:String(this.variant.id),quantity:Number(this.form.elements.quantity.value)};
    if (this.plan?.value) item.selling_plan=Number(this.plan.value);
    const checkout=Boolean(e.submitter?.matches('[data-buy]'));
    this.busy=true;this.form.setAttribute('aria-busy','true');this.form.querySelector('.api__purchase-fields').disabled=true;
    this.querySelector('[data-button-label]').textContent=this.dataset.workingLabel;
    this.querySelector('[data-status]').textContent='';this.querySelector('[data-recovery]').hidden=true;
    const job=()=>this.add(item,checkout);
    AgrolixProductInformation.queue=AgrolixProductInformation.queue.then(job,job);
   }, {signal});
   if (this.dataset.updateUrl === 'true') window.addEventListener('popstate', () => {
    if(this.busy)return;
    const url=new URL(location.href), id=url.searchParams.get('variant')||this.initialId;
    const radio=[...this.form.querySelectorAll('[name=id]')].find(x=>x.value===id);
    if(radio){if(radio.type==='radio')radio.checked=true;else radio.value=id;this.updateVariant(false);if(this.plan){this.plan.value=url.searchParams.get('selling_plan')||'';if(!this.plan.value&&this.variant.requiresPlan)this.plan.selectedIndex=0;this.updatePrice(false);}}
   }, {signal});
   this.querySelector('[data-buy]')?.removeAttribute('hidden');
   this.updateVariant(false, true);
  }
  buildDescriptionDropdowns() {
   const original = this.querySelector('[data-description-headings]');
   const source = original?.querySelector('.api__richtext');
   const headings = source ? [...source.querySelectorAll('h3')] : [];
   if (!headings.length) return;
   const group = document.createElement('div');
   group.className = 'api__description-dropdowns';
   // DOM ranges preserve lists, links and nested rich text between headings.
   const takeBefore = (next) => {
    const range = document.createRange();
    range.setStart(source, 0);
    if (next) range.setEndBefore(next); else range.setEnd(source, source.childNodes.length);
    return range.extractContents();
   };
   const intro = document.createElement('div');
   intro.className = 'api__body api__richtext';
   intro.append(takeBefore(headings[0]));
   if (intro.textContent.trim() || intro.querySelector('img,video,iframe,table')) {
    const detail = document.createElement('details');
    detail.className = 'api__detail api__description-item';
    const summary = document.createElement('summary');
    summary.className = 'api__accordion';
    const title = document.createElement('h3');
    const originalTitle = original.querySelector('summary').cloneNode(true);
    originalTitle.querySelector('[aria-hidden]')?.remove();
    title.textContent = originalTitle.textContent.trim() || 'Description';
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';
    summary.append(title, icon);
    detail.append(summary, intro);
    group.append(detail);
   }
   headings.forEach((heading, index) => {
    const detail = document.createElement('details');
    detail.className = 'api__detail api__description-item';
    const summary = document.createElement('summary');
    summary.className = 'api__accordion';
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';
    summary.append(heading, icon);
    const body = document.createElement('div');
    body.className = 'api__body api__richtext';
    body.append(takeBefore(headings[index + 1]));
    detail.append(summary, body);
    group.append(detail);
   });
   original.replaceWith(group);
  }
  updateVariant(writeUrl=false,initial=false) {
   const id=String(this.form.elements.id.value);
   this.variant=this.variants.find(x=>String(x.id)===id);
   if(!this.variant)return;
   const v=this.variant;
   if(this.plan){
    const old=this.plan.value;this.plan.replaceChildren();
    if(!v.requiresPlan)this.plan.add(new Option(this.dataset.onceLabel||'One-time purchase',''));
    for(const p of v.plans)this.plan.add(new Option(p.name,String(p.id)));
    if([...this.plan.options].some(o=>o.value===old))this.plan.value=old;
    this.plan.required=v.requiresPlan;
    this.plan.closest('.api__plans').hidden=!v.requiresPlan&&!v.plans.length;
   }
   const qty=this.form.elements.quantity;
   qty.min=v.min;qty.step=v.step;if(v.max)qty.max=v.max;else qty.removeAttribute('max');
   let value=Math.max(v.min,Number(qty.value)||v.min);value=v.min+Math.ceil((value-v.min)/v.step)*v.step;
   if(v.max)value=Math.min(value,v.max);qty.value=value;
   this.querySelector('[data-selected]').textContent=v.title==='Default Title'?'':v.title;
   const sku=this.querySelector('[data-sku]');if(sku){sku.textContent='SKU: '+(v.sku||'');sku.hidden=!v.sku;}
   if(!initial)this.showMedia(String(v.media));
   this.updatePrice(writeUrl);this.updateQuantityButtons();
  }
  updatePrice(writeUrl=false) {
   const v=this.variant;if(!v)return;
   const plan=v.plans.find(x=>String(x.id)===this.plan?.value), p=plan||v;
   for(const variant of this.variants){const price=this.querySelector(`[data-variant-price="${variant.id}"]`);if(price){const choice=variant.plans.find(x=>String(x.id)===this.plan?.value)||(variant.requiresPlan?variant.plans[0]:null);price.textContent=(choice||variant).money;}}
   const sale=Number(p.compare)>Number(p.price);
   this.querySelector('[data-price]').textContent=p.money;
   this.querySelector('[data-compare]').textContent=p.compareMoney;
   this.querySelector('[data-compare]').hidden=!sale;
   this.querySelector('[data-saving]').hidden=!sale;
   this.querySelector('[data-saving-money]').textContent=p.savingMoney;
   this.querySelector('[data-percent]').textContent=sale?(Math.round((p.compare-p.price)/p.compare*100)||'<1'):'';
   const unit=this.querySelector('[data-unit]');unit.textContent=plan?'':v.unit;unit.hidden=!unit.textContent;
   const desc=this.querySelector('[data-plan-description]');if(desc)desc.textContent=plan?.description||'';
   const available=v.available&&(!v.requiresPlan||Boolean(plan));
   this.querySelector('[data-add]').disabled=!available;
   const buy=this.querySelector('[data-buy]');if(buy)buy.disabled=!available;
   this.querySelector('[data-button-label]').textContent=available?this.dataset.addLabel:this.dataset.soldLabel;
   this.querySelector('[data-availability]').textContent=available?this.dataset.availableLabel:this.dataset.unavailableLabel;
   this.querySelector('[data-status]').textContent='';this.querySelector('[data-recovery]').hidden=true;
   if(writeUrl&&this.dataset.updateUrl==='true') {
    const url=new URL(location.href);url.searchParams.set('variant',v.id);
    if(plan)url.searchParams.set('selling_plan',plan.id);else url.searchParams.delete('selling_plan');
    history.replaceState(history.state,'',url);
   }
  }
  updateQuantityButtons(){const q=this.form.elements.quantity,value=Number(q.value);this.querySelector('[data-quantity-change="-1"]').disabled=value<=Number(q.min);this.querySelector('[data-quantity-change="1"]').disabled=Boolean(q.max)&&value>=Number(q.max);}
  showMedia(id){
   if(!this.querySelector(`[data-media="${CSS.escape(id)}"]`))return;
   this.querySelectorAll('[data-media]').forEach(panel=>{
    const selected=panel.dataset.media===id;panel.hidden=!selected;
    if(!selected){panel.querySelectorAll('video').forEach(v=>v.pause());panel.querySelectorAll('iframe').forEach(f=>f.remove());panel.querySelector('[data-play]')?.removeAttribute('hidden');}
   });
   this.querySelectorAll('[data-thumbnail]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.thumbnail===id)));
  }
  openModal(modal,trigger){this.returnFocus=trigger;if(!modal.open){this.savedOverflow=document.documentElement.style.overflow;document.documentElement.style.overflow='hidden';modal.showModal();modal.querySelector('button')?.focus();}}
  unlockScroll(){if(this.savedOverflow!==undefined)document.documentElement.style.overflow=this.savedOverflow;this.savedOverflow=undefined;}
  async add(item,checkout){
   let added=false;
   try{
    const native=document.querySelector('theme-drawer#cart-drawer');
    const ids=[...new Set([...(native?['cart-drawer-section']:[]),...[...document.querySelectorAll('cart-items-component[data-section-id]')].map(x=>x.dataset.sectionId)])].slice(0,5);
    const body={items:[item],sections_url:location.pathname};if(ids.length&&!checkout)body.sections=ids;
    const response=await fetch(this.dataset.addUrl+'.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)});
    const result=await response.json();
    if(!response.ok||result.status){const error=new Error(typeof result.description==='string'?result.description:this.dataset.failedLabel);error.shopify=true;throw error;}
    added=true;
    if(checkout){this.leaving=true;HTMLFormElement.prototype.submit.call(this.querySelector('[data-checkout-form]'));return;}
    const cartResponse=await fetch(this.dataset.cartUrl+'.js',{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
    if(!cartResponse.ok)throw new Error(this.dataset.refreshLabel);
    const cart=await cartResponse.json();if(!Array.isArray(cart.items))throw new Error(this.dataset.refreshLabel);
    let opened=false;
    if(native&&result.sections?.['cart-drawer-section']){
     try{
      const {morphSection}=await import(this.dataset.morphUrl);
      await morphSection('cart-drawer-section',result.sections['cart-drawer-section'],{mode:'hydration',injectStylesheet:true});
      const drawer=document.querySelector('theme-drawer#cart-drawer');
      if(typeof drawer?.open==='function'&&this.isConnected){await this.notifyCart(cart,result.sections,drawer.querySelector('cart-items-component')||this,'add',item);drawer.open();opened=true;}
     }catch(_){/* Fall back to the current cart if native hydration is unavailable. */}
    }
    if(!opened){await this.notifyCart(cart,result.sections,this,'update',item);if(this.isConnected){if(typeof native?.close==='function')native.close();this.renderFallback(cart,this.querySelector('[data-add]'));}}
    this.querySelector('[data-status]').textContent=this.dataset.addedLabel;
   }catch(error){this.leaving=false;this.querySelector('[data-status]').textContent=added?this.dataset.refreshLabel:(error.shopify?error.message:this.dataset.failedLabel);this.querySelector('[data-recovery]').hidden=false;}
   finally{
    if(!this.leaving){this.busy=false;this.form.removeAttribute('aria-busy');this.form.querySelector('.api__purchase-fields').disabled=false;const available=this.variant.available&&(!this.variant.requiresPlan||Boolean(this.plan?.value));this.querySelector('[data-add]').disabled=!available;const buy=this.querySelector('[data-buy]');if(buy)buy.disabled=!available;this.querySelector('[data-button-label]').textContent=available?this.dataset.addLabel:this.dataset.soldLabel;this.updateQuantityButtons();}
   }
  }
  async notifyCart(cart, sections, target, action, item) {
   const detail = { items: cart.items, itemCount: cart.item_count, sections, source: 'agrolix-product-information', didError: false };
   let event;
   try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    event = new CartLinesUpdateEvent({ action, context: 'product', lines: [{ merchandiseId: String(item.id), quantity: item.quantity }], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail }) });
   } catch (_) {
    event = new Event('shopify:cart:lines-update', { bubbles: true });
    Object.assign(event, { action, context: 'product', lines: [{ merchandiseId: String(item.id), quantity: item.quantity }], promise: Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail }) });
   }
   target.dispatchEvent(event);
  }
  money(cents, currency) { const code = currency || this.dataset.currency || 'INR'; return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: code }).formatToParts(cents / 100).map(part => part.type === 'currency' && code === 'INR' ? '₹' : part.value).join(''); }
  renderFallback(cart, trigger) {
   const list = this.querySelector('[data-cart-items]');
   list.replaceChildren();
   for (const item of cart.items) {
    const row = document.createElement('div'); row.className = 'api__drawer-item';
    const img = document.createElement('img'); img.alt = ''; img.width = 76; img.height = 76;
    if (item.image) { img.src = item.image; img.loading = 'lazy'; row.append(img); }
    const text = document.createElement('div');
    const link = document.createElement('a'); link.href = item.url || this.dataset.cartUrl; link.textContent = item.product_title || item.title; text.append(link);
    if (item.variant_title && item.variant_title !== 'Default Title') { const variant = document.createElement('p'); variant.textContent = item.variant_title; text.append(variant); }
    const price = document.createElement('p'); price.textContent = `${item.quantity} × ${this.money(item.final_price, cart.currency)}`; text.append(price);
    const total = document.createElement('strong'); total.textContent = this.money(item.final_line_price, cart.currency); text.append(total);
    row.append(text); list.append(row);
   }
   this.querySelector('[data-subtotal]').textContent = this.money(cart.total_price, cart.currency);
   this.returnFocus = trigger;
   if (!this.dialog.open) {
    this.savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.querySelector('[data-close]').focus();
   }
  }
  disconnectedCallback(){
   this.querySelectorAll('video').forEach(v=>v.pause());
   this.querySelectorAll('iframe').forEach(f=>f.remove());
   if(this.dialog?.open)this.dialog.close();if(this.zoom?.open)this.zoom.close();this.unlockScroll();this.abort?.abort();this.abort=null;
  }
 }
 customElements.define('agrolix-product-information',AgrolixProductInformation);
}
